import React, { createContext, useState, useEffect, ReactNode } from 'react';
import { apiService } from '@api/apiService';
import { StorageUtils } from '@utils/storageUtils';
import { AuthContextType, UserProfile } from '@/types';

export const AuthContext = createContext<AuthContextType | null>(null);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userData, setUserData] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if token exists and validate it
    const initializeAuth = async () => {
      const token = StorageUtils.getAuthToken();

      if (token) {
        try {
          const response = await apiService.getUserProfile();
          setUserData(response.data.user);
          setIsAuthenticated(true);
        } catch (error) {
          // Token is invalid or expired
          StorageUtils.clear();
          setIsAuthenticated(false);
          setUserData(null);
        }
      }

      setLoading(false);
    };

    initializeAuth();
  }, []);

  const login = async (email: string, password: string): Promise<void> => {
    try {
      // Login and get token
      const loginResponse = await apiService.userLogin(email, password);
      const { accessToken } = loginResponse.data;

      // Store token
      StorageUtils.setAuthToken(accessToken);

      // Get admin profile
      const profileResponse = await apiService.getUserProfile();
      setUserData(profileResponse.data.user);
      setIsAuthenticated(true);
    } catch (error) {
      // Clear any stored token on login failure
      StorageUtils.clear();
      throw error;
    }
  };

  const logout = (): void => {
    StorageUtils.clear();
    setIsAuthenticated(false);
    setUserData(null);
  };

  const value: AuthContextType = {
    isAuthenticated,
    userData,
    login,
    logout,
    loading,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};